import React, { createContext, useContext, useState, useCallback, useMemo } from 'react'

export type CartItem = {
  id: string // local cart item id
  modelId?: string
  modelName: string
  fileUrl?: string
  thumbnailUrl?: string
  materialId: string
  materialName: string
  colorId?: string
  colorName?: string
  colorHex?: string
  qualityPresetId: string
  qualityName: string
  infillOptionId: string
  infillLabel: string
  postProcessingIds: string[]
  postProcessingNames: string[]
  quantity: number
  unitPrice: number // exc VAT
  unitPriceIncVat: number
  totalPrice: number
  vatAmount: number
  volume_cm3?: number
  note?: string
  companyId?: string | null
  companyName?: string
  // snapshots for order
  materialSnapshot?: any
  colorSnapshot?: any
  qualitySnapshot?: any
  infillSnapshot?: any
  postProcessingSnapshot?: any
}

type CartContextValue = {
  items: CartItem[]
  itemCount: number
  subtotal: number
  vat: number
  total: number
  addItem: (item: Omit<CartItem, 'id'>) => void
  updateQuantity: (id: string, quantity: number) => void
  removeItem: (id: string) => void
  clearCart: () => void
}

const CartContext = createContext<CartContextValue | undefined>(undefined)

export function CartProvider({ children }: { children: React.ReactNode }) {
  const [items, setItems] = useState<CartItem[]>([])

  const addItem = useCallback((item: Omit<CartItem, 'id'>) => {
    const id = `cart_${Date.now()}_${Math.random().toString(36).slice(2)}`
    setItems((prev) => [...prev, { ...item, id }])
  }, [])

  const updateQuantity = useCallback((id: string, quantity: number) => {
    if (quantity <= 0) {
      setItems((prev) => prev.filter((i) => i.id !== id))
      return
    }
    setItems((prev) =>
      prev.map((i) =>
        i.id === id
          ? {
              ...i,
              quantity,
              totalPrice: i.unitPrice * quantity,
              vatAmount: (i.unitPriceIncVat - i.unitPrice) * quantity,
            }
          : i
      )
    )
  }, [])

  const removeItem = useCallback((id: string) => {
    setItems((prev) => prev.filter((i) => i.id !== id))
  }, [])

  const clearCart = useCallback(() => setItems([]), [])

  const itemCount = useMemo(() => items.reduce((sum, i) => sum + i.quantity, 0), [items])
  const subtotal = useMemo(() => items.reduce((sum, i) => sum + i.unitPrice * i.quantity, 0), [items])
  const vat = useMemo(() => items.reduce((sum, i) => sum + (i.unitPriceIncVat - i.unitPrice) * i.quantity, 0), [items])
  const total = useMemo(() => subtotal + vat, [subtotal, vat])

  const value = useMemo<CartContextValue>(
    () => ({ items, itemCount, subtotal, vat, total, addItem, updateQuantity, removeItem, clearCart }),
    [items, itemCount, subtotal, vat, total, addItem, updateQuantity, removeItem, clearCart]
  )

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>
}

export function useCart() {
  const ctx = useContext(CartContext)
  if (!ctx) throw new Error('useCart must be used within CartProvider')
  return ctx
}
